/**
 * [UX-C2 · #295 W06 · UX-29/30/52] queries ของกติกะจริงของการ์ด
 * - composeContextQuery: GET /api/v1/tickets/:id/compose-context (staff) — field definitions (typed)
 *   + transition requirements · fail เงียบได้ (หน้าการ์ดเดิมทำงานต่อ — สิทธิ์/endpoint ยังใหม่)
 * - useServicePriorities: ตัวเลือกความสำคัญตาม scheme ของบริการจาก GET /api/v1/services (items[].priorities)
 *   เลิก hardcode low/normal/high/urgent ทุกจุดของหน้าการ์ด (UX-29)
 */
import { useQuery } from "@tanstack/react-query";
import type { PriorityRef, TicketComposeContext } from "@uwu/contracts";
import { api } from "./api";
import { servicesQuery } from "./queries";

export const composeKeys = {
	context: (ticketId: string) => ["compose-context", ticketId] as const,
};

export const composeContextQuery = (ticketId: string) => ({
	queryKey: composeKeys.context(ticketId),
	queryFn: () => api.get<TicketComposeContext>(`/api/v1/tickets/${encodeURIComponent(ticketId)}/compose-context`),
	enabled: ticketId.length > 0,
	staleTime: 10_000,
	retry: false,
});

export type ServiceWithPriorities = { id: string; code: string; name: Record<string, string>; priorities?: PriorityRef[] };

/** ตัวเลือก priority ของ scheme ที่บริการนี้ผูกไว้ (worker เรียงตาม rank + fallback default scheme ให้แล้ว) */
export function useServicePriorities(serviceId: string | null | undefined) {
	const q = useQuery({ ...servicesQuery, select: (list: ServiceWithPriorities[]) => (serviceId ? (list.find((s) => s.id === serviceId)?.priorities ?? []) : []) });
	return { options: q.data ?? [], isPending: q.isPending, isError: q.isError, refetch: q.refetch };
}

/** compose-context เข้าถึงได้แบบ null-safe — UI เดิมทำงานต่อเมื่อยังโหลดไม่ได้ */
export function useComposeContext(ticketId: string | null | undefined) {
	const q = useQuery({ ...composeContextQuery(ticketId ?? ""), enabled: !!ticketId });
	return { data: q.data ?? null, isPending: q.isPending && !!ticketId, isError: q.isError, refetch: q.refetch };
}

/** requirements ของ transition ไปยังสถานะนี้ — null = ไม่มีข้อมูล/ไม่มี requirements */
export function transitionRequirementsOf(ctx: TicketComposeContext | null, toStatusCode: string) {
	return ctx?.transitions?.find((tr) => tr.to_status_code === toStatusCode) ?? null;
}

/** transition นี้ต้องเปิด TransitionDialog ไหม — ตัดสินจาก contract requirements เท่านั้น (UX-52) */
export function transitionNeedsDialog(req: ReturnType<typeof transitionRequirementsOf>): boolean {
	return !!req && (req.require_note || (req.required_fields ?? []).length > 0 || req.requires_approval);
}
